<template>
  <div>
    <div class="page-header">
      <div class="add-item d-flex">
        <div class="page-title">
          <h4>{{ campaign.name || $t('campaign_details') }}</h4>
          <span v-if="campaign.effective_status" class="badge" :class="statusClass(campaign.effective_status)">
            {{ statusLabel(campaign.effective_status) }}
          </span>
        </div>
      </div>
      <div class="page-btn d-flex gap-2 flex-wrap">
        <router-link to="/discount/campaigns" class="btn btn-outline-secondary">Back</router-link>
        <router-link
          v-if="canEdit"
          :to="{ name: 'DiscountCampaignEdit', params: { id } }"
          class="btn btn-primary"
        >
          {{ $t('edit') }}
        </router-link>
        <button v-if="canActivate" type="button" class="btn btn-success" :disabled="acting" @click="doAction('activate')">
          {{ $t('activate') }}
        </button>
        <button v-if="canPause" type="button" class="btn btn-warning" :disabled="acting" @click="doAction('pause')">
          {{ $t('pause') }}
        </button>
        <button v-if="canCancel" type="button" class="btn btn-outline-danger" :disabled="acting" @click="doAction('cancel')">
          {{ $t('cancel') }}
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-muted p-4">Loading...</div>
    <template v-else>
      <div class="card mb-3">
        <div class="card-body">
          <div class="row">
            <div class="col-md-6 mb-2"><strong>{{ $t('campaign_name') }}:</strong> {{ campaign.name }}</div>
            <div class="col-md-6 mb-2"><strong>{{ $t('priority') }}:</strong> {{ campaign.priority }}</div>
            <div class="col-md-6 mb-2"><strong>{{ $t('discount_type') }}:</strong> {{ campaign.discount_type_display }}</div>
            <div class="col-md-6 mb-2"><strong>{{ $t('discount_value') }}:</strong> {{ formatDiscountValue(campaign) }}</div>
            <div class="col-md-6 mb-2"><strong>{{ $t('start_date_time') }}:</strong> {{ formatDateTime(campaign.start_at) }}</div>
            <div class="col-md-6 mb-2"><strong>{{ $t('end_date_time') }}:</strong> {{ formatDateTime(campaign.end_at) }}</div>
            <div class="col-md-6 mb-2"><strong>{{ $t('created_by') }}:</strong> {{ campaign.created_by_name || '—' }}</div>
            <div class="col-md-6 mb-2">
              <strong>{{ $t('stores') }}:</strong>
              {{ storeLabel }}
            </div>
            <div class="col-12 mb-2"><strong>{{ $t('description') }}:</strong> {{ campaign.description || '—' }}</div>
          </div>
        </div>
      </div>

      <div class="card">
        <div class="card-body">
          <h5 class="mb-3">{{ $t('selected_items') }} ({{ (campaign.items || []).length }})</h5>
          <div class="table-responsive">
            <table class="table table-hover">
              <thead class="thead-light">
                <tr>
                  <th>{{ $t('item_name') }}</th>
                  <th>{{ $t('sku') }} / {{ $t('barcode') }}</th>
                  <th>{{ $t('variant') }}</th>
                  <th>{{ $t('selling_price') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in campaign.items || []" :key="item.id">
                  <td>{{ item.item_name }}</td>
                  <td>{{ item.sku || item.barcode || '—' }}</td>
                  <td>{{ item.variant_label || '—' }}</td>
                  <td>৳{{ Number(item.regular_selling_price || 0).toFixed(2) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { useToast } from 'vue-toastification';
import { can as canAccess } from '@/helpers/permission';
import apiUrls from '@/urls';

export default {
  name: 'DiscountCampaignDetail',
  props: {
    id: { type: [String, Number], required: true },
  },
  data() {
    return {
      toast: useToast(),
      loading: true,
      acting: false,
      campaign: {},
    };
  },
  computed: {
    storeLabel() {
      const stores = this.campaign.stores || [];
      if (!stores.length) return this.$t('all_authorized_stores');
      return stores.map((s) => s.name).join(', ');
    },
    canEdit() {
      return canAccess('inventory.discount_campaign', 'E')
        && !['cancelled', 'expired'].includes(this.campaign.effective_status);
    },
    canActivate() {
      return canAccess('inventory.discount_campaign', 'W')
        && ['draft', 'paused', 'scheduled'].includes(this.campaign.effective_status);
    },
    canPause() {
      return canAccess('inventory.discount_campaign', 'W')
        && ['active', 'scheduled'].includes(this.campaign.effective_status);
    },
    canCancel() {
      return canAccess('inventory.discount_campaign', 'W')
        && !['cancelled', 'expired'].includes(this.campaign.effective_status);
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    statusLabel(status) {
      const map = {
        draft: this.$t('status_draft'),
        scheduled: this.$t('status_scheduled'),
        active: this.$t('status_active'),
        paused: this.$t('status_paused'),
        expired: this.$t('status_expired'),
        cancelled: this.$t('status_cancelled'),
      };
      return map[status] || status;
    },
    statusClass(status) {
      return {
        draft: 'bg-secondary',
        scheduled: 'bg-info',
        active: 'bg-success',
        paused: 'bg-warning text-dark',
        expired: 'bg-dark',
        cancelled: 'bg-danger',
      }[status] || 'bg-secondary';
    },
    formatDateTime(value) {
      if (!value) return '—';
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
    },
    formatDiscountValue(row) {
      const v = Number(row.discount_value || 0);
      if (row.discount_type === 'percentage') return `${v}%`;
      return `৳${v.toFixed(2)}`;
    },
    async load() {
      this.loading = true;
      try {
        const { data } = await this.$axios.get(apiUrls.discount_campaign.detail(this.id));
        this.campaign = data;
      } catch (_) {
        this.toast.error(this.$t('something_went_wrong'));
      } finally {
        this.loading = false;
      }
    },
    async doAction(action) {
      const messages = {
        activate: this.$t('confirm_activate_campaign'),
        pause: this.$t('confirm_pause_campaign'),
        cancel: this.$t('confirm_cancel_campaign'),
      };
      if (!window.confirm(messages[action])) return;
      this.acting = true;
      try {
        const { data } = await this.$axios.post(apiUrls.discount_campaign[action](this.id));
        this.campaign = data;
        this.toast.success(this.$t('campaign_updated'));
      } catch (err) {
        this.toast.error(err?.response?.data?.detail || this.$t('something_went_wrong'));
      } finally {
        this.acting = false;
      }
    },
  },
};
</script>
